import { API_BASE_URL } from './api';

const CS_MKT_ID_KEY = 'checkstall_cs_mkt_id_v1';
const FIRST_TOUCH_KEY = 'checkstall_marketing_first_touch_v1';
const LATEST_TOUCH_KEY = 'checkstall_marketing_latest_touch_v1';
const SESSION_ID_KEY = 'checkstall_marketing_session_id_v1';

const MARKETING_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'fbclid',
  'gclid',
  'ttclid',
  'campaign_id',
  'adset_id',
  'ad_id',
  'creative_id',
  'meta_campaign_id',
  'meta_adset_id',
  'meta_ad_id',
];

const memory = new Map();
const storage = {
  get(key) {
    try {
      return window.localStorage.getItem(key) || memory.get(key) || null;
    } catch {
      return memory.get(key) || null;
    }
  },
  set(key, value) {
    memory.set(key, value);
    try {
      window.localStorage.setItem(key, value);
    } catch {
      // Marketing storage must stay best-effort.
    }
  },
};

const sessionStorageSafe = {
  get(key) {
    try {
      return window.sessionStorage.getItem(key) || memory.get(key) || null;
    } catch {
      return memory.get(key) || null;
    }
  },
  set(key, value) {
    memory.set(key, value);
    try {
      window.sessionStorage.setItem(key, value);
    } catch {
      // Fall through; session identity is best-effort.
    }
  },
};

const uuid = () => {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
};

export const getOrCreateCsMktId = () => {
  const existing = getStoredCsMktId();
  if (existing) {
    return existing;
  }
  const value = `csm_${uuid()}`;
  storage.set(CS_MKT_ID_KEY, value);
  return value;
};

export const getStoredCsMktId = () => {
  const existing = storage.get(CS_MKT_ID_KEY);
  const normalized = existing ? String(existing).trim() : '';
  return normalized || null;
};

export const getOrCreateMarketingSessionId = () => {
  const existing = sessionStorageSafe.get(SESSION_ID_KEY);
  if (existing) {
    return existing;
  }
  const value = `session_${uuid()}`;
  sessionStorageSafe.set(SESSION_ID_KEY, value);
  return value;
};

const readTouch = (key) => {
  try {
    const value = JSON.parse(storage.get(key));
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    return Object.fromEntries([...MARKETING_KEYS, 'landing_url', 'referrer', 'captured_at']
      .filter((key) => typeof value[key] === 'string').map((key) => [key, value[key]]));
  } catch { return null; }
};

let entryReferrerHandled = false;
export const getCurrentTouch = () => {
  const params = new URLSearchParams(window.location.search);
  const campaign = Object.fromEntries(MARKETING_KEYS.filter((key) => params.get(key))
    .map((key) => [key, params.get(key)]));
  for (const key of ['campaign_id', 'adset_id', 'ad_id']) {
    if (!campaign[key] && campaign['meta_' + key]) campaign[key] = campaign['meta_' + key];
  }
  let externalReferrer = false;
  try {
    const host = new URL(document.referrer).hostname;
    externalReferrer = host !== window.location.hostname && host !== 'checkstall.com' && !host.endsWith('.checkstall.com');
  } catch { /* Empty or invalid referrer. */ }
  let first = readTouch(FIRST_TOUCH_KEY);
  let latest = readTouch(LATEST_TOUCH_KEY);
  if (Object.keys(campaign).length || (!entryReferrerHandled && externalReferrer)) {
    const candidate = { ...campaign, landing_url: window.location.href, referrer: document.referrer || '' };
    const sameTouch = latest && Object.keys(candidate).every((key) => latest[key] === candidate[key]) &&
      MARKETING_KEYS.every((key) => latest[key] === candidate[key]);
    if (!sameTouch) {
      latest = { ...candidate, captured_at: new Date().toISOString() };
      storage.set(LATEST_TOUCH_KEY, JSON.stringify(latest));
    }
    if (!first) {
      first = latest;
      storage.set(FIRST_TOUCH_KEY, JSON.stringify(first));
    }
  }
  entryReferrerHandled = true;
  const id = getOrCreateCsMktId();
  return {
    ...(latest || first || {}), first_touch: first, latest_touch: latest,
    cs_mkt_id: id, anonymous_id: id, install_id: id,
    session_id: getOrCreateMarketingSessionId(), platform: 'public_web',
    page: window.location.pathname, page_url: window.location.href,
    landing_url: latest?.landing_url || first?.landing_url || window.location.href,
    referrer: latest?.referrer || first?.referrer || document.referrer || '',
  };
};

const buildEventPayload = (eventName, touch) => ({
  event_name: eventName,
  event_id: uuid(),
  properties: touch,
});

const sendAttributionPayload = async (eventName, touch, options = {}) => {
  const payload = buildEventPayload(eventName, touch);
  const url = `${API_BASE_URL}/app/marketing/attribution/events/`;

  if (options.preferBeacon && navigator.sendBeacon) {
    try {
      const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
      if (navigator.sendBeacon(url, blob)) {
        return null;
      }
    } catch {
      // Fall back to fetch below.
    }
  }

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Checkstall-Marketing-Id': touch.cs_mkt_id,
      'X-Marketing-Anonymous-Id': touch.cs_mkt_id,
      'X-Marketing-Install-Id': touch.cs_mkt_id,
      'X-Marketing-Session-Id': touch.session_id,
    },
    body: JSON.stringify(payload),
    keepalive: true,
  });
  if (!response.ok) throw new Error('Marketing API HTTP ' + response.status);
  return response.json().catch(() => null);
};

// Existing route analytics owns GA4 page views; do not emit a second GA page_view.
export const capturePublicAttribution = (eventName = 'landing_page_view', options = {}) =>
  trackPublicAttributionEvent(eventName, {}, { preferBeacon: false, ...options, ga: false });

export const trackPublicAttributionEvent = async (eventName, properties = {}, options = {}) => {
  if (!eventName) return null;
  const touch = { ...(options.touch || getCurrentTouch()), ...properties };
  if (options.ga !== false) {
    // Never forward browser IDs, raw URLs, click IDs or nested attribution to GA.
    const allowed = ['page', 'cta_location', 'destination', 'store', 'trigger', 'percent_scrolled',
      'engaged_ms', 'total_engaged_ms', 'flush_reason', 'utm_source', 'utm_medium', 'utm_campaign',
      'utm_content', 'campaign_id', 'adset_id', 'ad_id'];
    const gaProperties = Object.fromEntries(allowed.filter((key) => touch[key] != null)
      .map((key) => [key, touch[key]]));
    try {
      const gtag = window.gtag || function () {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(arguments);
      };
      gtag('event', eventName, gaProperties);
    } catch { /* A blocked GA tag must not block the custom API or navigation. */ }
  }
  try {
    const data = await sendAttributionPayload(eventName, touch, { preferBeacon: true, ...options });
    if (data?.cs_mkt_id) storage.set(CS_MKT_ID_KEY, data.cs_mkt_id);
    return { captured: true, data };
  } catch {
    // Best effort: never retry navigation events blindly or block the CTA.
    return { captured: false };
  }
};

export const getMarketingHeaders = () => ({
  'X-Checkstall-Marketing-Id': getOrCreateCsMktId(),
  'X-Marketing-Anonymous-Id': getOrCreateCsMktId(),
  'X-Marketing-Install-Id': getOrCreateCsMktId(),
  'X-Marketing-Session-Id': getOrCreateMarketingSessionId(),
});

export const appendCsMktId = (url) => {
  try {
    const nextUrl = new URL(url, window.location.origin);
    nextUrl.searchParams.set('cs_mkt_id', getOrCreateCsMktId());
    return nextUrl.toString();
  } catch {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}cs_mkt_id=${encodeURIComponent(getOrCreateCsMktId())}`;
  }
};

export const appendExistingCsMktId = (url) => {
  const existing = getStoredCsMktId();
  if (!existing) {
    return url;
  }

  try {
    const nextUrl = new URL(url, window.location.origin);
    nextUrl.searchParams.set('cs_mkt_id', existing);
    return nextUrl.toString();
  } catch {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}cs_mkt_id=${encodeURIComponent(existing)}`;
  }
};

export const appendPlayStoreReferrer = (url) => {
  const referrer = new URLSearchParams({
    cs_mkt_id: getOrCreateCsMktId(),
  }).toString();
  try {
    const nextUrl = new URL(url);
    nextUrl.searchParams.set('referrer', referrer);
    return nextUrl.toString();
  } catch {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}referrer=${encodeURIComponent(referrer)}`;
  }
};
